/**
 * Proposals Page
 * Proposal management for designers (incoming requests + submit) and homeowners (review + accept/reject)
 */

import { useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import {
	ArrowLeft,
	BadgeCheck,
	Calendar,
	CheckCircle2,
	ChevronDown,
	FileText,
	Inbox,
	Send,
	XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuthStore } from "@/features/auth/store/auth.store";
import {
	acceptProposal,
	getDesignerRequests,
	getProjectProposals,
	getProjects,
	type Project,
	type ProjectProposalItem,
	type ProjectRequest,
	rejectProposal,
	submitProposal,
} from "@/features/projects/services/projects-api.service";
import { FloatPanel, StudioButton } from "@/features/studio/theme";
import { formatPrice } from "@/lib/format";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import {
	type ProposalDecision,
	ProposalDecisionDialog,
} from "../components/homeowner/proposals/proposal-decision-dialog";
import {
	ProposalDetail,
	ProposalSummary,
} from "../components/homeowner/proposals/proposal-detail";

// ============================================================================
// HELPERS
// ============================================================================

function formatDate(dateString: string): string {
	return new Date(dateString).toLocaleDateString("en-IN", {
		day: "numeric",
		month: "short",
		year: "numeric",
	});
}

function getRequestStatusColor(status: string): string {
	switch (status) {
		case "pending":
			return "bg-dashboard-pill-awaiting-bg text-dashboard-warning";
		case "proposal_submitted":
			return "bg-dashboard-pill-info-bg text-dashboard-info-text";
		case "accepted":
			return "bg-dashboard-pill-done-bg text-dashboard-success-strong";
		case "rejected":
			return "bg-dashboard-pill-risk-bg text-dashboard-danger-strong";
		default:
			return "bg-muted text-foreground";
	}
}

function getRequestStatusLabel(status: string, t: TFunction): string {
	switch (status) {
		case "pending":
			return String(t("status.pending", { defaultValue: "Pending" }));
		case "proposal_submitted":
			return String(
				t("status.proposalSubmitted", { defaultValue: "Proposal Submitted" }),
			);
		case "accepted":
			return String(t("status.accepted", { defaultValue: "Accepted" }));
		case "rejected":
			return String(t("status.rejected", { defaultValue: "Rejected" }));
		default:
			return status;
	}
}

// ============================================================================
// DESIGNER VIEW — Incoming Requests + Submit Proposals
// ============================================================================

export function DesignerProposalsView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [requests, setRequests] = useState<ProjectRequest[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [expandedRequestId, setExpandedRequestId] = useState<string | null>(
		null,
	);
	const [proposalForm, setProposalForm] = useState({
		costEstimate: "",
		timelineWeeks: "",
		scopeDescription: "",
		approach: "",
		notes: "",
		costBreakdownDesignFees: "",
		costBreakdownLabor: "",
		costBreakdownMaterials: "",
		costBreakdownOther: "",
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [loadFailed, setLoadFailed] = useState(false);

	// `getDesignerRequests` throws on a failed read. Catching here keeps the
	// failure off the empty state below, which would otherwise tell a designer
	// whose request list did not load that nobody has sent them anything.
	const loadRequests = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await getDesignerRequests();
			setRequests(data);
			setLoadFailed(false);
		} catch {
			setLoadFailed(true);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		loadRequests();
	}, [loadRequests]);

	const resetForm = () => {
		setProposalForm({
			costEstimate: "",
			timelineWeeks: "",
			scopeDescription: "",
			approach: "",
			notes: "",
			costBreakdownDesignFees: "",
			costBreakdownLabor: "",
			costBreakdownMaterials: "",
			costBreakdownOther: "",
		});
		setSubmitError(null);
	};

	const handleToggleForm = (requestId: string) => {
		if (expandedRequestId === requestId) {
			setExpandedRequestId(null);
		} else {
			setExpandedRequestId(requestId);
		}
		resetForm();
	};

	const handleSubmitProposal = async (requestId: string) => {
		const cost = parseFloat(proposalForm.costEstimate);
		const weeks = parseInt(proposalForm.timelineWeeks, 10);

		if (!cost || cost <= 0) {
			setSubmitError(t("proposals.validCostError"));
			return;
		}
		if (!weeks || weeks <= 0) {
			setSubmitError(t("proposals.validTimelineError"));
			return;
		}

		setIsSubmitting(true);
		setSubmitError(null);

		// Build costBreakdown from individual fields, omitting empty values
		const costBreakdown: Record<string, number> = {};
		if (proposalForm.costBreakdownDesignFees)
			costBreakdown.designFees = parseInt(
				proposalForm.costBreakdownDesignFees,
				10,
			);
		if (proposalForm.costBreakdownLabor)
			costBreakdown.labor = parseInt(proposalForm.costBreakdownLabor, 10);
		if (proposalForm.costBreakdownMaterials)
			costBreakdown.materials = parseInt(
				proposalForm.costBreakdownMaterials,
				10,
			);
		if (proposalForm.costBreakdownOther)
			costBreakdown.other = parseInt(proposalForm.costBreakdownOther, 10);

		try {
			const result = await submitProposal(requestId, {
				costEstimate: cost,
				timelineWeeks: weeks,
				scopeDescription: proposalForm.scopeDescription || undefined,
				approach: proposalForm.approach || undefined,
				notes: proposalForm.notes || undefined,
				costBreakdown:
					Object.keys(costBreakdown).length > 0 ? costBreakdown : undefined,
			});

			if (result.success) {
				setExpandedRequestId(null);
				resetForm();
				await loadRequests();
			} else {
				setSubmitError(result.error || t("proposals.failedToSubmit"));
			}
		} catch {
			setSubmitError(t("proposals.unexpectedError"));
		} finally {
			setIsSubmitting(false);
		}
	};

	// Loading skeleton
	if (isLoading) {
		return (
			<div className="space-y-4">
				{[1, 2, 3].map((i) => (
					<Skeleton key={i} className="h-40 w-full rounded-lg" />
				))}
			</div>
		);
	}

	if (loadFailed) {
		return (
			<QueryErrorState
				message={t(
					"proposals.requestsLoadError",
					"Failed to load your requests. Please try again.",
				)}
				onRetry={loadRequests}
			/>
		);
	}

	// Empty state
	if (requests.length === 0) {
		return (
			<FloatPanel
				elevation={1}
				padding={40}
				style={{
					background: "var(--color-dashboard-paper)",
					textAlign: "center",
				}}
			>
				<div className="mb-4 flex justify-center">
					<Inbox className="h-12 w-12 text-[var(--color-dashboard-muted)]" />
				</div>
				<h3
					style={{
						fontFamily: "var(--font-display)",
						fontWeight: 600,
						fontSize: 18,
						color: "var(--color-dashboard-text)",
					}}
				>
					{t("proposals.noRequestsYet")}
				</h3>
				<p
					style={{
						fontFamily: "var(--font-body)",
						fontSize: 14,
						color: "var(--color-dashboard-muted)",
						marginTop: 8,
					}}
				>
					{t("proposals.noRequestsDesc")}
				</p>
			</FloatPanel>
		);
	}

	const getStatusPillStyles = (status: string) => {
		switch (status) {
			case "pending":
				return {
					background:
						"color-mix(in srgb, var(--color-dashboard-pill-awaiting) 10%, transparent)",
					color: "var(--color-dashboard-pill-awaiting)",
				};
			case "proposal_submitted":
				return {
					background:
						"color-mix(in srgb, var(--color-dashboard-pill-info) 10%, transparent)",
					color: "var(--color-dashboard-pill-info)",
				};
			case "accepted":
				return {
					background:
						"color-mix(in srgb, var(--color-dashboard-pill-done) 10%, transparent)",
					color: "var(--color-dashboard-pill-done)",
				};
			case "rejected":
				return {
					background:
						"color-mix(in srgb, var(--color-dashboard-pill-risk) 10%, transparent)",
					color: "var(--color-dashboard-pill-risk)",
				};
			default:
				return {
					background: "var(--color-dashboard-divider)",
					color: "var(--color-dashboard-text)",
				};
		}
	};

	return (
		<div className="space-y-4">
			{requests.map((request) => (
				<FloatPanel
					key={request.id}
					elevation={1}
					padding={24}
					style={{ background: "var(--color-dashboard-paper)" }}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							borderBottom: "1px solid var(--color-dashboard-border-soft)",
							paddingBottom: 14,
							marginBottom: 16,
						}}
					>
						<div>
							<h3
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 13,
									fontWeight: 600,
									color: "var(--color-dashboard-accent-dark)",
									textTransform: "uppercase",
								}}
							>
								{t("dashboard.projectRequest")}
							</h3>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: 6,
									marginTop: 4,
									color: "var(--color-dashboard-muted)",
									fontSize: 12,
									fontFamily: "var(--font-body)",
								}}
							>
								<Calendar
									className="h-3.5 w-3.5"
									style={{ color: "var(--color-dashboard-accent-dark)" }}
								/>
								{t("common.received", {
									date: formatDate(request.createdAt),
								})}
							</div>
						</div>
						<span
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 9,
								letterSpacing: "0.08em",
								fontWeight: 600,
								padding: "4px 8px",
								borderRadius: 14,
								textTransform: "uppercase",
								...getStatusPillStyles(request.status),
							}}
						>
							{getRequestStatusLabel(request.status, t)}
						</span>
					</div>

					<div style={{ marginBottom: 20 }}>
						{request.message ? (
							<div
								style={{
									background: "var(--color-dashboard-paper)",
									border: "1px solid var(--color-dashboard-border)",
									borderRadius: 8,
									padding: "14px 16px",
								}}
							>
								<p
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 13.5,
										lineHeight: 1.6,
										color: "var(--color-dashboard-text)",
									}}
								>
									{request.message}
								</p>
							</div>
						) : (
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 13,
									fontStyle: "italic",
									color: "var(--color-dashboard-muted)",
								}}
							>
								{t("proposals.noMessageFromHomeowner")}
							</p>
						)}
					</div>

					<div className="flex flex-col items-stretch gap-4">
						{/* Pending — show Submit Proposal toggle */}
						{request.status === "pending" && (
							<>
								<StudioButton
									variant={
										expandedRequestId === request.id ? "ghost" : "primary"
									}
									size="sm"
									onClick={() => handleToggleForm(request.id)}
									style={{
										display: "inline-flex",
										alignSelf: "flex-start",
										gap: 6,
									}}
								>
									<Send className="h-4 w-4" />
									{expandedRequestId === request.id
										? t("common.cancel")
										: t("proposals.submitProposal")}
								</StudioButton>

								{expandedRequestId === request.id && (
									<div
										className="w-full space-y-4 rounded-xl border border-dashboard-border p-4"
										style={{ background: "var(--color-dashboard-paper)" }}
									>
										<div className="grid gap-4 sm:grid-cols-2">
											<div className="space-y-1.5">
												<Label
													htmlFor={`cost-${request.id}`}
													style={{
														fontFamily: "var(--font-display)",
														fontWeight: 600,
														fontSize: 13,
														color: "var(--color-dashboard-text)",
													}}
												>
													{t("proposals.costEstimateLabel")}
												</Label>
												<Input
													id={`cost-${request.id}`}
													type="number"
													min="0"
													placeholder="e.g. 500000"
													value={proposalForm.costEstimate}
													onChange={(e) =>
														setProposalForm((prev) => ({
															...prev,
															costEstimate: e.target.value,
														}))
													}
													style={{
														width: "100%",
														padding: "10px 14px",
														border: "1px solid var(--color-border-default)",
														borderRadius: 7,
														background: "var(--color-dashboard-paper)",
														fontSize: 14,
														color: "var(--color-dashboard-text)",
														outline: "none",
													}}
												/>
											</div>
											<div className="space-y-1.5">
												<Label
													htmlFor={`timeline-${request.id}`}
													style={{
														fontFamily: "var(--font-display)",
														fontWeight: 600,
														fontSize: 13,
														color: "var(--color-dashboard-text)",
													}}
												>
													{t("proposals.timelineWeeksLabel")}
												</Label>
												<Input
													id={`timeline-${request.id}`}
													type="number"
													min="1"
													placeholder="e.g. 12"
													value={proposalForm.timelineWeeks}
													onChange={(e) =>
														setProposalForm((prev) => ({
															...prev,
															timelineWeeks: e.target.value,
														}))
													}
													style={{
														width: "100%",
														padding: "10px 14px",
														border: "1px solid var(--color-border-default)",
														borderRadius: 7,
														background: "var(--color-dashboard-paper)",
														fontSize: 14,
														color: "var(--color-dashboard-text)",
														outline: "none",
													}}
												/>
											</div>
										</div>

										<div className="space-y-1.5">
											<Label
												htmlFor={`scope-${request.id}`}
												style={{
													fontFamily: "var(--font-display)",
													fontWeight: 600,
													fontSize: 13,
													color: "var(--color-dashboard-text)",
												}}
											>
												{t("proposals.scopeDescription")}
											</Label>
											<Textarea
												id={`scope-${request.id}`}
												placeholder={t("proposals.scopePlaceholder")}
												rows={4}
												value={proposalForm.scopeDescription}
												onChange={(e) =>
													setProposalForm((prev) => ({
														...prev,
														scopeDescription: e.target.value,
													}))
												}
												style={{
													width: "100%",
													padding: "10px 14px",
													border: "1px solid var(--color-border-default)",
													borderRadius: 7,
													background: "var(--color-dashboard-paper)",
													fontSize: 14,
													color: "var(--color-dashboard-text)",
													outline: "none",
													resize: "vertical",
												}}
											/>
										</div>

										<div className="space-y-1.5">
											<Label
												htmlFor={`approach-${request.id}`}
												style={{
													fontFamily: "var(--font-display)",
													fontWeight: 600,
													fontSize: 13,
													color: "var(--color-dashboard-text)",
												}}
											>
												{t("proposals.approachLabel")}
											</Label>
											<Textarea
												id={`approach-${request.id}`}
												placeholder={t("proposals.approachPlaceholder")}
												rows={3}
												value={proposalForm.approach}
												onChange={(e) =>
													setProposalForm((prev) => ({
														...prev,
														approach: e.target.value,
													}))
												}
												style={{
													width: "100%",
													padding: "10px 14px",
													border: "1px solid var(--color-border-default)",
													borderRadius: 7,
													background: "var(--color-dashboard-paper)",
													fontSize: 14,
													color: "var(--color-dashboard-text)",
													outline: "none",
													resize: "vertical",
												}}
											/>
										</div>

										<Collapsible>
											<CollapsibleTrigger
												style={{
													display: "flex",
													width: "100%",
													alignItems: "center",
													justifyContent: "space-between",
													borderRadius: 7,
													border: "1px solid var(--color-border-default)",
													background: "var(--color-dashboard-paper)",
													padding: "10px 14px",
													fontFamily: "var(--font-display)",
													fontWeight: 600,
													fontSize: 13,
													color: "var(--color-dashboard-text)",
													cursor: "pointer",
												}}
											>
												{t("proposals.costBreakdownLabel")}
												<ChevronDown
													className="h-4 w-4"
													style={{
														color: "var(--color-dashboard-accent-dark)",
													}}
												/>
											</CollapsibleTrigger>
											<CollapsibleContent className="pt-3">
												<div className="grid gap-3 sm:grid-cols-2">
													<CurrencyInput
														id={`cb-design-${request.id}`}
														label={t("proposals.costBreakdownDesignFees")}
														value={proposalForm.costBreakdownDesignFees}
														onChange={(val) =>
															setProposalForm((prev) => ({
																...prev,
																costBreakdownDesignFees: val,
															}))
														}
														placeholder="0"
													/>
													<CurrencyInput
														id={`cb-labor-${request.id}`}
														label={t("proposals.costBreakdownLabor")}
														value={proposalForm.costBreakdownLabor}
														onChange={(val) =>
															setProposalForm((prev) => ({
																...prev,
																costBreakdownLabor: val,
															}))
														}
														placeholder="0"
													/>
													<CurrencyInput
														id={`cb-materials-${request.id}`}
														label={t("proposals.costBreakdownMaterials")}
														value={proposalForm.costBreakdownMaterials}
														onChange={(val) =>
															setProposalForm((prev) => ({
																...prev,
																costBreakdownMaterials: val,
															}))
														}
														placeholder="0"
													/>
													<CurrencyInput
														id={`cb-other-${request.id}`}
														label={t("proposals.costBreakdownOther")}
														value={proposalForm.costBreakdownOther}
														onChange={(val) =>
															setProposalForm((prev) => ({
																...prev,
																costBreakdownOther: val,
															}))
														}
														placeholder="0"
													/>
												</div>
											</CollapsibleContent>
										</Collapsible>

										<div className="space-y-1.5">
											<Label
												htmlFor={`notes-${request.id}`}
												style={{
													fontFamily: "var(--font-display)",
													fontWeight: 600,
													fontSize: 13,
													color: "var(--color-dashboard-text)",
												}}
											>
												{t("proposals.notesLabel")}
											</Label>
											<Textarea
												id={`notes-${request.id}`}
												placeholder={t("proposals.notesPlaceholder")}
												rows={2}
												value={proposalForm.notes}
												onChange={(e) =>
													setProposalForm((prev) => ({
														...prev,
														notes: e.target.value,
													}))
												}
												style={{
													width: "100%",
													padding: "10px 14px",
													border: "1px solid var(--color-border-default)",
													borderRadius: 7,
													background: "var(--color-dashboard-paper)",
													fontSize: 14,
													color: "var(--color-dashboard-text)",
													outline: "none",
													resize: "vertical",
												}}
											/>
										</div>

										{submitError && (
											<p
												className="text-sm text-destructive"
												style={{
													fontFamily: "var(--font-body)",
													fontWeight: 500,
												}}
											>
												{submitError}
											</p>
										)}

										<StudioButton
											variant="primary"
											size="sm"
											disabled={isSubmitting}
											onClick={() => handleSubmitProposal(request.id)}
										>
											{isSubmitting
												? "Submitting..."
												: t("proposals.submitProposal")}
										</StudioButton>
									</div>
								)}
							</>
						)}

						{/* Proposal Submitted */}
						{request.status === "proposal_submitted" && (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
									color: "var(--color-dashboard-pill-info)",
									fontSize: 14,
									fontFamily: "var(--font-body)",
								}}
							>
								<CheckCircle2 className="h-4 w-4" />
								{t("proposals.proposalSubmittedAwaiting")}
							</div>
						)}

						{/* Accepted */}
						{request.status === "accepted" && (
							<div style={{ display: "grid", gap: 12 }}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 8,
										color: "var(--color-dashboard-pill-done)",
										fontSize: 14,
										fontFamily: "var(--font-body)",
									}}
								>
									<CheckCircle2 className="h-4 w-4" />
									{t("proposals.proposalAcceptedDesigner")}
								</div>
								<p
									style={{
										margin: 0,
										fontSize: 13,
										fontFamily: "var(--font-body)",
										color: "var(--color-dashboard-muted-dark)",
									}}
								>
									{t("proposals.acceptedNextStep")}
								</p>
								<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
									<StudioButton
										variant="primary"
										size="sm"
										onClick={() =>
											navigate(`/projects/${request.projectId}/boqs/new`)
										}
									>
										{t("proposals.createBoq")}
									</StudioButton>
									<StudioButton
										variant="secondary"
										size="sm"
										onClick={() =>
											navigate(
												`/projects/${request.projectId}/boqs/new?mode=starter`,
											)
										}
									>
										{t("proposals.createBoqFromTemplate")}
									</StudioButton>
								</div>
							</div>
						)}

						{/* Rejected */}
						{request.status === "rejected" && (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
									color: "var(--color-dashboard-pill-risk)",
									fontSize: 14,
									fontFamily: "var(--font-body)",
								}}
							>
								<XCircle className="h-4 w-4" />
								{t("proposals.requestDeclined")}
							</div>
						)}
					</div>
				</FloatPanel>
			))}
		</div>
	);
}

// ============================================================================
// HOMEOWNER VIEW — Review Proposals + Accept/Reject
// ============================================================================

interface ProjectWithProposals {
	project: Project;
	proposals: ProjectProposalItem[];
}

/**
 * An accept/reject failure, tagged with the proposal it belongs to so the
 * message can outlive the loading id that the handler clears on settle.
 */
interface ProposalActionError {
	proposalId: string;
	message: string;
}

function HomeownerProposalsView() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [projectProposals, setProjectProposals] = useState<
		ProjectWithProposals[]
	>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
	const [actionError, setActionError] = useState<ProposalActionError | null>(
		null,
	);
	const [acceptedProposalId, setAcceptedProposalId] = useState<string | null>(
		null,
	);
	const [decision, setDecision] = useState<
		(ProposalDecision & { proposalId: string }) | null
	>(null);

	const loadProposals = useCallback(async () => {
		setIsLoading(true);
		try {
			const projects = await getProjects();
			const seekingProjects = projects.filter(
				(p) => p.status === "seeking_designer",
			);

			const grouped: ProjectWithProposals[] = await Promise.all(
				seekingProjects.map(async (project) => {
					const items = (await getProjectProposals(
						project.id,
					)) as ProjectProposalItem[];
					return { project, proposals: items };
				}),
			);

			// Only show projects that have at least one proposal with a submitted proposal
			setProjectProposals(grouped.filter((g) => g.proposals.length > 0));
		} catch {
			console.error("Failed to load proposals");
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		loadProposals();
	}, [loadProposals]);

	const handleAccept = async (proposalId?: string) => {
		if (!proposalId) return;
		setActionLoadingId(proposalId);
		setActionError(null);

		try {
			const result = await acceptProposal(proposalId);
			if (result.success) {
				void queryClient.invalidateQueries({
					queryKey: queryKeys.projects.lists(),
				});
				setAcceptedProposalId(proposalId);
			} else {
				setActionError({
					proposalId,
					message: result.error || t("proposals.failedToSubmit"),
				});
			}
		} catch {
			setActionError({ proposalId, message: t("proposals.unexpectedError") });
		} finally {
			setActionLoadingId(null);
		}
	};

	const handleReject = async (proposalId?: string) => {
		if (!proposalId) return;
		setActionLoadingId(proposalId);
		setActionError(null);

		try {
			const result = await rejectProposal(proposalId);
			if (result.success) {
				await loadProposals();
			} else {
				setActionError({
					proposalId,
					message: result.error || t("proposals.failedToSubmit"),
				});
			}
		} catch {
			setActionError({ proposalId, message: t("proposals.unexpectedError") });
		} finally {
			setActionLoadingId(null);
		}
	};

	const requestDecision = (
		mode: ProposalDecision["mode"],
		item: ProjectProposalItem,
	) => {
		if (!item.proposal) return;
		setDecision({
			mode,
			proposalId: item.proposal.id,
			designerName: item.designer.name,
			costEstimate: item.proposal.costEstimate,
		});
	};

	const confirmDecision = () => {
		if (!decision) return;
		if (decision.mode === "accept") {
			void handleAccept(decision.proposalId);
		} else {
			void handleReject(decision.proposalId);
		}
	};

	// Loading skeleton
	if (isLoading) {
		return (
			<div className="space-y-6">
				<Skeleton className="h-6 w-48 rounded" />
				{[1, 2].map((i) => (
					<Skeleton key={i} className="h-52 w-full rounded-lg" />
				))}
			</div>
		);
	}

	// Success state after accepting a proposal
	if (acceptedProposalId) {
		return (
			<Card className="p-12 text-center">
				<div className="mb-4 flex justify-center">
					<CheckCircle2 className="h-12 w-12 text-dashboard-success" />
				</div>
				<h3 className="text-lg font-semibold text-foreground">
					{t("proposals.proposalAccepted")}
				</h3>
				<p className="mt-2 text-sm text-muted-foreground">
					{t("proposals.proposalAcceptedDesc")}
				</p>
				<Button
					variant="primary"
					size="sm"
					className="mt-6"
					onClick={() => {
						setAcceptedProposalId(null);
						loadProposals();
					}}
				>
					{t("proposals.viewMoreProposals")}
				</Button>
			</Card>
		);
	}

	// Empty state
	if (projectProposals.length === 0) {
		return (
			<Card className="p-12 text-center">
				<div className="mb-4 flex justify-center">
					<FileText className="h-12 w-12 text-muted-foreground" />
				</div>
				<h3 className="text-lg font-semibold text-foreground">
					{t("proposals.noProposalsYet")}
				</h3>
				<p className="mt-2 text-sm text-muted-foreground">
					{t("proposals.noProposalsDesc")}
				</p>
			</Card>
		);
	}

	return (
		<div className="space-y-8">
			{projectProposals.map(({ project, proposals }) => (
				<div key={project.id}>
					{/* Project heading */}
					<div className="mb-3">
						<h2 className="text-lg font-semibold text-foreground">
							{project.title}
						</h2>
						<p className="text-sm text-muted-foreground">
							{project.scope === "full_home" ? "Full Home" : "Partial"}
							{project.budgetMin && project.budgetMax
								? ` — Budget: ${formatPrice(project.budgetMin)} - ${formatPrice(project.budgetMax)}`
								: ""}
						</p>
					</div>

					{/* Proposal cards for this project */}
					<div className="space-y-4">
						{proposals.map((item) => {
							const proposalId = item.proposal?.id;
							const isCurrentAction = actionLoadingId === proposalId;
							const canAct =
								item.request.status === "proposal_submitted" &&
								item.proposal !== null;

							return (
								<Card key={item.request.id}>
									<CardHeader>
										<div className="flex items-start justify-between gap-3">
											<div className="flex items-center gap-3">
												{/* Designer avatar */}
												<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium">
													{item.designer.name.charAt(0).toUpperCase()}
												</div>
												<div>
													<div className="flex items-center gap-2">
														<CardTitle className="text-base">
															{item.designer.name}
														</CardTitle>
														{item.designer.isVerified && (
															<BadgeCheck className="h-4 w-4 shrink-0 text-primary" />
														)}
													</div>
													{item.designer.firmName && (
														<CardDescription>
															{item.designer.firmName}
														</CardDescription>
													)}
												</div>
											</div>
											<span
												className={cn(
													"shrink-0 rounded-full px-2.5 py-1 text-xs font-medium",
													getRequestStatusColor(item.request.status),
												)}
											>
												{getRequestStatusLabel(item.request.status, t)}
											</span>
										</div>
									</CardHeader>

									{/* Proposal details */}
									{item.proposal && (
										<CardContent className="space-y-4">
											<ProposalSummary proposal={item.proposal} />
											<ProposalDetail proposal={item.proposal} />
										</CardContent>
									)}

									{/* Accept / Reject buttons for actionable proposals */}
									{canAct && (
										<CardFooter className="flex flex-col items-stretch gap-3">
											{actionError && actionError.proposalId === proposalId && (
												<p className="text-sm text-destructive">
													{actionError.message}
												</p>
											)}
											<div className="flex gap-3">
												<Button
													variant="primary"
													size="sm"
													isLoading={isCurrentAction}
													onClick={() => requestDecision("accept", item)}
													disabled={!!actionLoadingId}
												>
													<CheckCircle2 className="h-4 w-4" />
													{t("proposals.accept")}
												</Button>
												<Button
													variant="destructive"
													size="sm"
													isLoading={isCurrentAction}
													onClick={() => requestDecision("reject", item)}
													disabled={!!actionLoadingId}
												>
													<XCircle className="h-4 w-4" />
													{t("proposals.reject")}
												</Button>
											</div>
										</CardFooter>
									)}

									{/* Terminal status indicators */}
									{item.request.status === "accepted" && (
										<CardFooter>
											<div className="flex items-center gap-2 text-sm text-dashboard-success">
												<CheckCircle2 className="h-4 w-4" />
												{t("proposals.proposalAcceptedStatus")}
											</div>
										</CardFooter>
									)}

									{item.request.status === "rejected" && (
										<CardFooter>
											<div className="flex items-center gap-2 text-sm text-dashboard-pill-risk">
												<XCircle className="h-4 w-4" />
												{t("proposals.proposalRejectedStatus")}
											</div>
										</CardFooter>
									)}
								</Card>
							);
						})}
					</div>
				</div>
			))}
			<ProposalDecisionDialog
				decision={decision}
				onConfirm={confirmDecision}
				onCancel={() => setDecision(null)}
			/>
		</div>
	);
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export function ProposalsPage() {
	const { t } = useTranslation();
	const { user } = useAuthStore();
	const navigate = useNavigate();
	const isDesigner = user?.userType === "designer";

	return (
		<div className="min-h-screen bg-muted">
			{/* Page header */}
			<div className="border-b bg-card">
				<div className="mx-auto max-w-3xl px-4 py-6">
					<button
						type="button"
						onClick={() => navigate("/dashboard")}
						className="mb-4 flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
					>
						<ArrowLeft className="h-4 w-4" />
						{t("common.backToDashboard")}
					</button>
					<h1 className="text-2xl font-bold text-foreground">
						{isDesigner
							? t("proposals.projectRequests")
							: t("proposals.proposalsTitle")}
					</h1>
					<p className="mt-1 text-muted-foreground">
						{isDesigner
							? t("proposals.designerSubtitle")
							: t("proposals.homeownerSubtitle")}
					</p>
				</div>
			</div>

			{/* Page content */}
			<div className="mx-auto max-w-3xl px-4 py-6">
				{isDesigner ? <DesignerProposalsView /> : <HomeownerProposalsView />}
			</div>
		</div>
	);
}
